/**
 * ChatMessage - Individual message in the chat
 *
 * Supports:
 * - User and assistant messages
 * - Agent attribution with avatar
 * - Streaming indicator (blinking cursor)
 * - Thinking state (collapsible section showing AI reasoning)
 * - File attachments display (HOME-10)
 * - Transcription display (HOME-11)
 */

import { useState } from 'react';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Attachment metadata for files uploaded with messages.
 * Implements HOME-10: Attachments support PDF, DOC/DOCX, images, and video
 */
export interface MessageAttachment {
  /** Unique identifier (R2 key) */
  id: string;
  /** Original filename */
  name: string;
  /** Attachment type category */
  type: 'image' | 'document' | 'video' | 'audio';
  /** URL to access the file */
  url: string;
  /** MIME type (e.g., 'image/png', 'application/pdf') */
  mimeType: string;
  /** File size in bytes */
  size: number;
  /** Transcription text for video/audio files (HOME-11) */
  transcription?: string;
}

/**
 * Chat message structure.
 * Used for both user messages and AI assistant responses.
 */
export interface Message {
  /** Unique message identifier */
  id: string;
  /** Message role: user, assistant, or system */
  role: 'user' | 'assistant' | 'system';
  /** Message content (text) */
  content: string;
  /** Agent ID if message is from a specific agent */
  agentId?: string;
  /** Human-readable agent name for display */
  agentName?: string;
  /** When the message was created */
  createdAt: Date;
  /** True if this message is currently streaming */
  isStreaming?: boolean;
  /** AI thinking/reasoning (collapsible) */
  thinking?: string;
  /** File attachments with this message */
  attachments?: MessageAttachment[];
}

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface ChatMessageProps {
  /** The message to display */
  message: Message;
  /** Callback when user requests transcription of a video/audio file */
  onTranscribe?: (attachmentId: string) => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format file size in human-readable format.
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get icon class name based on file type.
 * @param type - Attachment type category
 * @returns CSS class name for icon styling
 */
function getFileIcon(type: MessageAttachment['type']): string {
  switch (type) {
    case 'image':
      return 'image';
    case 'video':
      return 'video';
    case 'audio':
      return 'audio';
    default:
      return 'document';
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ChatMessage({ message, onTranscribe }: ChatMessageProps) {
  // State for showing/hiding thinking section
  const [showThinking, setShowThinking] = useState(false);

  // Determine if this is a user or assistant message
  const isUser = message.role === 'user';

  return (
    <div className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
      {/* Avatar section */}
      <div className="message-avatar">
        {isUser ? (
          // User avatar - simple person icon
          <div className="avatar-user">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </div>
        ) : (
          // Assistant avatar - globe icon with optional agent badge
          <div className="avatar-assistant">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
            </svg>
            {/* Show agent name badge if from specific agent */}
            {message.agentName && (
              <span className="agent-badge">{message.agentName}</span>
            )}
          </div>
        )}
      </div>

      {/* Message content section */}
      <div className="message-content">
        {/* Thinking section (for assistant messages) */}
        {message.thinking && !isUser && (
          <button
            type="button"
            className="thinking-toggle"
            onClick={() => setShowThinking(!showThinking)}
          >
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className={`chevron ${showThinking ? 'expanded' : ''}`}
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
            Thinking
          </button>
        )}

        {/* Expanded thinking content */}
        {showThinking && message.thinking && (
          <div className="thinking-content">{message.thinking}</div>
        )}

        {/* Attachments display (HOME-10) */}
        {message.attachments && message.attachments.length > 0 && (
          <div className="message-attachments">
            {message.attachments.map((attachment) => (
              <div key={attachment.id} className="attachment-item">
                {/* Image attachments show as thumbnails */}
                {attachment.type === 'image' ? (
                  <img
                    src={attachment.url}
                    alt={attachment.name}
                    className="attachment-image"
                  />
                ) : (
                  // Non-image files show as file cards
                  <div className="attachment-file">
                    <span
                      className={`file-icon file-icon-${getFileIcon(attachment.type)}`}
                    />
                    <div className="file-info">
                      <a
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {attachment.name}
                      </a>
                      <span className="file-size">
                        {formatSize(attachment.size)}
                      </span>
                    </div>
                    {/* Transcribe button for video/audio (HOME-11) */}
                    {(attachment.type === 'video' ||
                      attachment.type === 'audio') &&
                      !attachment.transcription &&
                      onTranscribe && (
                        <button
                          type="button"
                          className="transcribe-btn"
                          onClick={() => onTranscribe(attachment.id)}
                          title="Transcribe audio/video"
                        >
                          Transcribe
                        </button>
                      )}
                  </div>
                )}

                {/* Show transcription if available */}
                {attachment.transcription && (
                  <div className="attachment-transcription">
                    <strong>Transcription:</strong>
                    <p>{attachment.transcription}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Main message text */}
        <div className="message-text">
          {message.content}
          {/* Blinking cursor for streaming messages */}
          {message.isStreaming && <span className="streaming-cursor">|</span>}
        </div>
      </div>

      {/* Inline styles for chat message component */}
      <style>{`
        .chat-message {
          display: flex;
          gap: 12px;
          padding: 12px 16px;
        }

        .chat-message.user {
          background: var(--color-surface-muted);
        }

        .message-avatar {
          flex-shrink: 0;
        }

        .avatar-user, .avatar-assistant {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .avatar-user {
          background: var(--color-border);
          color: var(--color-text-secondary);
        }

        .avatar-user svg, .avatar-assistant svg {
          width: 18px;
          height: 18px;
        }

        .avatar-assistant {
          background: var(--color-accent);
          color: white;
        }

        .agent-badge {
          position: absolute;
          bottom: -4px;
          right: -4px;
          background: var(--color-text);
          color: white;
          font-size: 9px;
          padding: 1px 4px;
          border-radius: 4px;
          white-space: nowrap;
        }

        .message-content {
          flex: 1;
          min-width: 0;
        }

        .thinking-toggle {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          margin-bottom: 8px;
          border: none;
          border-radius: 4px;
          background: var(--color-surface-muted);
          color: var(--color-text-secondary);
          font-size: 12px;
          cursor: pointer;
        }

        .thinking-toggle:hover {
          background: var(--color-border);
        }

        .thinking-toggle .chevron {
          width: 14px;
          height: 14px;
          transition: transform 0.15s;
        }

        .thinking-toggle .chevron.expanded {
          transform: rotate(180deg);
        }

        .thinking-content {
          padding: 8px 12px;
          margin-bottom: 8px;
          background: var(--color-surface-muted);
          border-left: 2px solid var(--color-border-strong);
          font-size: 12px;
          color: var(--color-text-secondary);
          white-space: pre-wrap;
        }

        .message-attachments {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 8px;
        }

        .attachment-item {
          max-width: 200px;
        }

        .attachment-image {
          max-width: 100%;
          border-radius: 8px;
        }

        .attachment-file {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: var(--color-surface-muted);
          border: 1px solid var(--color-border);
          border-radius: 8px;
        }

        .file-info {
          display: flex;
          flex-direction: column;
        }

        .file-info a {
          font-size: 13px;
          color: var(--color-primary);
          text-decoration: none;
        }

        .file-info a:hover {
          text-decoration: underline;
        }

        .file-size {
          font-size: 11px;
          color: var(--color-text-muted);
        }

        .transcribe-btn {
          padding: 4px 8px;
          font-size: 11px;
          border: 1px solid var(--color-accent);
          border-radius: 4px;
          background: var(--color-surface);
          color: var(--color-accent);
          cursor: pointer;
        }

        .transcribe-btn:hover {
          background: var(--color-primary-soft);
        }

        .attachment-transcription {
          margin-top: 8px;
          padding: 8px;
          background: var(--color-primary-soft);
          border-radius: 4px;
          font-size: 12px;
        }

        .message-text {
          font-size: 14px;
          line-height: 1.6;
          color: var(--color-text);
          white-space: pre-wrap;
          word-break: break-word;
        }

        .streaming-cursor {
          animation: blink 1s infinite;
          color: var(--color-accent);
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
